import React, { useState, useMemo } from 'react';
import './Bmi.css';

const BmiCalculator = () => {
  const [gender, setGender] = useState('female'); // 'male' | 'female' | 'transgender'
  const [heightUnit, setHeightUnit] = useState('cm'); // 'cm' | 'ft_in'
  const [showInfo, setShowInfo] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Height values
  const [heightCm, setHeightCm] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  
  // Age and Weight
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');

  // Compute BMI and associated stats
  const stats = useMemo(() => {
    // 1. Calculate height in meters
    let hMeters = 0;
    if (heightUnit === 'cm') {
      hMeters = (parseFloat(heightCm) || 0) / 100;
    } else {
      const feet = parseFloat(heightFt) || 0;
      const inches = parseFloat(heightIn) || 0;
      hMeters = (feet * 12 + inches) * 0.0254;
    }

    const wKg = parseFloat(weight) || 0;

    // 2. BMI Formula
    let bmi = 0;
    if (hMeters > 0 && wKg > 0) {
      bmi = wKg / (hMeters * hMeters);
    }

    // 3. BMI Category
    let category = 'Normal';
    let dotColorClass = 'bmi-dot-normal';
    if (bmi > 0) {
      if (bmi < 18.5) {
        category = 'Under Weight';
        dotColorClass = 'bmi-dot-under';
      } else if (bmi < 25) {
        category = 'Normal';
        dotColorClass = 'bmi-dot-normal';
      } else if (bmi < 30) {
        category = 'Over Weight';
        dotColorClass = 'bmi-dot-over';
      } else {
        category = 'Obesity';
        dotColorClass = 'bmi-dot-obese';
      }
    }

    // 4. Healthy BMI ranges & weights (18.5 to 24.9)
    let minHealthyWeight = 0;
    let maxHealthyWeight = 0;
    if (hMeters > 0) {
      minHealthyWeight = 18.5 * hMeters * hMeters;
      maxHealthyWeight = 24.9 * hMeters * hMeters;
    }

    // 5. Action
    let actionText = 'Maintain weight';
    let actionClass = 'maintain';
    if (bmi > 0) {
      if (bmi < 18.5 && minHealthyWeight > 0) {
        const diff = Math.round(minHealthyWeight - wKg);
        actionText = `Gain ${diff} Kg`;
        actionClass = 'gain';
      } else if (bmi >= 25 && maxHealthyWeight > 0) {
        const diff = Math.round(wKg - maxHealthyWeight);
        actionText = `Lose ${diff} Kg`;
        actionClass = 'lose';
      }
    }

    // 6. Gauge Needle Angle calculation (piecewise linear to match segments)
    let angle = 90; // vertical up default when empty or uncalculated
    if (bmi > 0) {
      const clampedBmi = Math.max(10, Math.min(40, bmi));
      if (clampedBmi < 18.5) {
        // Under Weight segment (10 to 18.5) -> Maps to angles 180 to 135
        angle = 180 - ((clampedBmi - 10) / (18.5 - 10)) * 45;
      } else if (clampedBmi < 25) {
        // Normal segment (18.5 to 25) -> Maps to angles 135 to 90
        angle = 135 - ((clampedBmi - 18.5) / (25 - 18.5)) * 45;
      } else if (clampedBmi < 30) {
        // Over Weight segment (25 to 30) -> Maps to angles 90 to 45
        angle = 90 - ((clampedBmi - 25) / (30 - 25)) * 45;
      } else {
        // Obesity segment (30 to 40) -> Maps to angles 45 to 0
        angle = 45 - ((clampedBmi - 30) / (40 - 30)) * 45;
      }
    }

    return {
      bmi: parseFloat(bmi.toFixed(2)) || 0,
      category,
      dotColorClass,
      minHealthyWeight: Math.round(minHealthyWeight),
      maxHealthyWeight: Math.round(maxHealthyWeight),
      actionText,
      actionClass,
      needleAngle: angle
    };
  }, [heightUnit, heightCm, heightFt, heightIn, weight]);

  // Input Handlers
  const handleNumberInput = (setter, val, maxVal = 999) => {
    if (val === '') {
      setter('');
      return;
    }
    const parsed = parseFloat(val);
    if (isNaN(parsed)) {
      setter('');
    } else {
      const value = Math.max(0, parsed);
      setter(Math.min(maxVal, value));
    }
  };

  return (
    <div className="bmi-calculator-container">


      <div className="bmi-main-content">
        {/* Left Side: Inputs */}
        <div className="bmi-input-section">
          <div className="bmi-input-grid">
            {/* Gender selection */}
            <div className="bmi-input-group bmi-grid-full">
              <span className="bmi-group-label">
                <i className="fas fa-venus-mars" style={{marginRight: '6px', color: '#ae0a0b'}}></i> Gender
              </span>
              <div className="bmi-gender-selector">
                <button
                  type="button"
                  className={`bmi-gender-btn ${gender === 'male' ? 'active' : ''}`}
                  onClick={() => setGender('male')}
                >
                  <i className="fas fa-mars" style={{marginRight: '6px'}}></i> Male
                </button>
                <button
                  type="button"
                  className={`bmi-gender-btn ${gender === 'female' ? 'active' : ''}`}
                  onClick={() => setGender('female')}
                >
                  <i className="fas fa-venus" style={{marginRight: '6px'}}></i> Female
                </button>
                <button
                  type="button"
                  className={`bmi-gender-btn ${gender === 'transgender' ? 'active' : ''}`}
                  onClick={() => setGender('transgender')}
                >
                  <i className="fas fa-genderless" style={{marginRight: '4px', fontSize: '1rem'}}></i> Transgender
                </button>
              </div>
            </div>

            {/* Age Input Card */}
            <div className="bmi-input-group">
              <span className="bmi-group-label">
                <i className="fas fa-hourglass-half" style={{marginRight: '6px', color: '#ae0a0b'}}></i> Age
              </span>
              <div className="bmi-input-card">
                <span className="bmi-input-card-label">Age</span>
                <input
                  type="number"
                  className="bmi-card-input"
                  value={age}
                  onChange={(e) => handleNumberInput(setAge, e.target.value, 120)}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Weight Input Card */}
            <div className="bmi-input-group">
              <span className="bmi-group-label">
                <i className="fas fa-weight" style={{marginRight: '6px', color: '#ae0a0b'}}></i> Weight (Kg)
              </span>
              <div className="bmi-input-card">
                <span className="bmi-input-card-label">Weight</span>
                <input
                  type="number"
                  className="bmi-card-input"
                  value={weight}
                  onChange={(e) => handleNumberInput(setWeight, e.target.value, 500)}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Height Input Card */}
            <div className="bmi-input-group bmi-grid-full">
              <span className="bmi-group-label">
                <i className="fas fa-ruler-vertical" style={{marginRight: '6px', color: '#ae0a0b'}}></i> Height
              </span>
              <div className="bmi-input-card">
                <div className="bmi-input-card-row">
                  {heightUnit === 'cm' ? (
                    <div style={{ flex: 1 }}>
                      <input
                        type="number"
                        className="bmi-card-input"
                        value={heightCm}
                        onChange={(e) => handleNumberInput(setHeightCm, e.target.value, 300)}
                        placeholder="0"
                      />
                    </div>
                  ) : (
                    <div className="bmi-height-inputs-row">
                      <div className="bmi-height-subfield">
                        <input
                          type="number"
                          className="bmi-height-subfield-input"
                          value={heightFt}
                          onChange={(e) => handleNumberInput(setHeightFt, e.target.value, 10)}
                          placeholder="0"
                        />
                        <span className="bmi-height-subfield-label">ft</span>
                      </div>
                      <div className="bmi-height-subfield">
                        <input
                          type="number"
                          className="bmi-height-subfield-input"
                          value={heightIn}
                          onChange={(e) => handleNumberInput(setHeightIn, e.target.value, 11)}
                          placeholder="0"
                        />
                        <span className="bmi-height-subfield-label">in</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Custom Premium Unit Dropdown */}
                  <div className="bmi-custom-dropdown-container">
                    <button
                      type="button"
                      className={`bmi-custom-dropdown-trigger ${isDropdownOpen ? 'open' : ''}`}
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      <span>{heightUnit === 'cm' ? 'Centimeters' : 'Feet/Inches'}</span>
                      <i className={`fas fa-chevron-down bmi-chevron-icon ${isDropdownOpen ? 'rotate' : ''}`}></i>
                    </button>
                    
                    {isDropdownOpen && (
                      <>
                        <div className="bmi-dropdown-backdrop" onClick={() => setIsDropdownOpen(false)} />
                        <ul className="bmi-custom-dropdown-menu">
                          <li 
                            className={`bmi-custom-dropdown-item ${heightUnit === 'cm' ? 'selected' : ''}`}
                            onClick={() => {
                              setHeightUnit('cm');
                              setIsDropdownOpen(false);
                            }}
                          >
                            Centimeters
                            {heightUnit === 'cm' && <i className="fas fa-check check-icon"></i>}
                          </li>
                          <li 
                            className={`bmi-custom-dropdown-item ${heightUnit === 'ft_in' ? 'selected' : ''}`}
                            onClick={() => {
                              setHeightUnit('ft_in');
                              setIsDropdownOpen(false);
                            }}
                          >
                            Feet/Inches
                            {heightUnit === 'ft_in' && <i className="fas fa-check check-icon"></i>}
                          </li>
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Results & Chart */}
        <div className="bmi-result-section">
          <h2 className="bmi-result-header">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <i className="fas fa-heartbeat" style={{marginRight: '8px', color: '#ae0a0b'}}></i> BMI Result
            </span>
            <button 
              type="button" 
              className={`bmi-info-btn ${showInfo ? 'active' : ''}`}
              onClick={() => setShowInfo(!showInfo)}
              title="BMI શું છે?"
              aria-label="Info"
            >
              <i className="fas fa-info-circle"></i>
            </button>
          </h2>

          {/* Inline Expandable Details Panel */}
          <div className={`bmi-info-collapse-panel ${showInfo ? 'expanded' : ''}`}>
            <div className="bmi-info-collapse-inner">
              <h3 className="bmi-collapse-title">
                <i className="fas fa-info-circle" style={{ marginRight: '8px', color: '#ae0a0b' }}></i>
                BMI શું છે?
              </h3>
              <p className="bmi-modal-lead">
                <strong>BMI (Body Mass Index)</strong> એટલે કે શરીર દ્રવ્યમાન સૂચકાંક.
              </p>
              <p>
                આ એક એવી ગણતરી છે જે વ્યક્તિના વજન (Weight) અને <strong>ઊંચાઈ (Height)</strong>ના આધાર પર શરીરમાં ચરબી (Body Fat) અથવા શરીરની તંદુરસ્તીનું અંદાજ આપે છે.
              </p>
              <p>
                BMI નો ઉપયોગ સામાન્ય રીતે એ જાણવા માટે થાય છે કે વ્યક્તિનું વજન તેની ઊંચાઈ મુજબ યોગ્ય છે કે નહીં.
              </p>
              
              <div className="bmi-modal-divider"></div>
              
              <h4 className="bmi-modal-subtitle">BMI ની ગણતરી પછી મળેલા પરિણામના આધારે વ્યક્તિને નીચેની કેટેગરીમાં રાખવામાં આવે છે:</h4>
              <ul className="bmi-modal-list">
                <li><span className="bmi-list-bullet bullet-under"></span> ઓછું વજન (Underweight)</li>
                <li><span className="bmi-list-bullet bullet-normal"></span> સામાન્ય વજન (Normal Weight)</li>
                <li><span className="bmi-list-bullet bullet-over"></span> વધારાનું વજન (Overweight)</li>
                <li><span className="bmi-list-bullet bullet-obese"></span> સ્થૂલતા / મોટાપો (Obese)</li>
              </ul>
              
              <div className="bmi-modal-divider"></div>
              
              <p>
                વિશ્વના અલગ અલગ પ્રદેશો અને ઉંમર મુજબ BMI ના માપદંડ થોડા બદલાઈ શકે છે. કેટલીકવાર તેને વધુ વિગતવાર કેટેગરીમાં પણ વહેંચવામાં આવે છે, જેમ કે ખૂબ ઓછું વજન અથવા ખૂબ વધુ સ્થૂલતા.
              </p>
              
              <div className="bmi-modal-alert">
                <i className="fas fa-exclamation-circle alert-icon"></i>
                <p className="alert-text">
                  જોકે BMI સંપૂર્ણ રીતે શરીરની તંદુરસ્તી માપવાનું સચોટ સાધન નથી, છતાં તે આરોગ્ય સંબંધિત સમસ્યાઓનું પ્રાથમિક મૂલ્યાંકન કરવા માટે ખૂબ ઉપયોગી છે. કારણ કે ઓછું અથવા વધુ વજન બંને આરોગ્ય માટે ગંભીર સમસ્યાઓ ઊભી કરી શકે છે.
                </p>
              </div>
            </div>
          </div>

          <div className="bmi-results-wrapper">
            {/* Custom SVG Gauge Chart */}
            <div className="bmi-gauge-wrapper">
              <svg className="bmi-gauge-svg" viewBox="0 0 250 140">
                <defs>
                  {/* Clip path for the gauge to ensure perfect rounding and segments */}
                  <clipPath id="gauge-clip">
                    <path d="M 30 130 A 95 95 0 0 1 220 130" fill="none" stroke="black" strokeWidth="20" strokeLinecap="round" />
                  </clipPath>
                  {/* Gradient definitions for extremely attractive gauge segment gradients */}
                  <linearGradient id="bmi-blue-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#60a5fa" />
                    <stop offset="100%" stopColor="#2563eb" />
                  </linearGradient>
                  <linearGradient id="bmi-green-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#34d399" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                  <linearGradient id="bmi-yellow-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#fbbf24" />
                    <stop offset="100%" stopColor="#d97706" />
                  </linearGradient>
                  <linearGradient id="bmi-red-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f87171" />
                    <stop offset="100%" stopColor="#dc2626" />
                  </linearGradient>
                </defs>

                {/* Background Track (Soft grey arc underneath the colored segments for physical depth) */}
                <path
                  d="M 30 130 A 95 95 0 0 1 220 130"
                  fill="none"
                  stroke="#f1f5f9"
                  strokeWidth="24"
                  strokeLinecap="round"
                />

                {/* Segment 1: Blue (Under Weight, 180 to 135 deg) */}
                <path
                  d="M 30 130 A 95 95 0 0 1 57.83 62.83"
                  fill="none"
                  stroke="url(#bmi-blue-grad)"
                  strokeWidth="24"
                  strokeLinecap="round"
                />

                {/* Segment 4: Red (Obesity, 45 to 0 deg) */}
                <path
                  d="M 192.17 62.83 A 95 95 0 0 1 220 130"
                  fill="none"
                  stroke="url(#bmi-red-grad)"
                  strokeWidth="24"
                  strokeLinecap="round"
                />

                {/* Segment 2: Green (Normal, 135 to 90 deg) */}
                <path
                  d="M 57.83 62.83 A 95 95 0 0 1 125 35"
                  fill="none"
                  stroke="url(#bmi-green-grad)"
                  strokeWidth="24"
                  strokeLinecap="butt"
                />

                {/* Segment 3: Yellow (Over Weight, 90 to 45 deg) */}
                <path
                  d="M 125 35 A 95 95 0 0 1 192.17 62.83"
                  fill="none"
                  stroke="url(#bmi-yellow-grad)"
                  strokeWidth="24"
                  strokeLinecap="butt"
                />

                {/* Boundary Ticks / Diagnostic Ticks inside the arc */}
                <text x="76" y="86" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="800">18.5</text>
                <text x="125" y="62" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="800">25</text>
                <text x="174" y="86" textAnchor="middle" fill="#64748b" fontSize="8" fontWeight="800">30</text>

                {/* Sleek needle with custom pivot and pointer tip */}
                <g 
                  className="bmi-gauge-needle" 
                  style={{ transform: `rotate(${90 - stats.needleAngle}deg)` }}
                >
                  {/* Sleek luxury triangle needle */}
                  <polygon points="122.5,130 125,38 127.5,130" fill="#334155" />
                  {/* Highlighting edge */}
                  <polygon points="125,38 125,130 127.5,130" fill="#475569" />
                  
                  {/* Pivot base circle with premium metallic colors & glowing center */}
                  <circle cx="125" cy="130" r="11" fill="#1e293b" />
                  <circle cx="125" cy="130" r="8" fill="#475569" />
                  <circle cx="125" cy="130" r="5" fill="#e2e8f0" />
                  <circle cx="125" cy="130" r="2.5" fill="#ae0a0b" />
                </g>
              </svg>
            </div>

            {/* BMI Score Card */}
            <div className="bmi-score-card">
              <span className="bmi-score-label">Your BMI</span>
              <span className="bmi-score-value">{stats.bmi > 0 ? stats.bmi : '-'}</span>
            </div>
          </div>

          {/* Gauge Legend indicators */}
          <div className="bmi-legend-container">
            <div className="bmi-legend-item">
              <span className="bmi-legend-dot bmi-dot-under"></span>
              <span>Under Weight</span>
            </div>
            <div className="bmi-legend-item">
              <span className="bmi-legend-dot bmi-dot-normal"></span>
              <span>Normal</span>
            </div>
            <div className="bmi-legend-item">
              <span className="bmi-legend-dot bmi-dot-over"></span>
              <span>Over Weight</span>
            </div>
            <div className="bmi-legend-item">
              <span className="bmi-legend-dot bmi-dot-obese"></span>
              <span>Obesity</span>
            </div>
          </div>

          {/* Details & Target weight card */}
          <div className="bmi-details-card">
            <div className="bmi-details-row">
              <div className="bmi-detail-item">
                <span className="bmi-detail-label">Healthy BMI range</span>
                <span className="bmi-detail-value">18.5 - 24.9</span>
              </div>
              <div className="bmi-detail-item">
                <span className="bmi-detail-label">Healthy weight for this height</span>
                <span className="bmi-detail-value">
                  {stats.minHealthyWeight > 0 ? `${stats.minHealthyWeight} - ${stats.maxHealthyWeight} Kg` : '-'}
                </span>
              </div>
            </div>

            <div className="bmi-action-box">
              <span className="bmi-action-label">Action</span>
              <span className={`bmi-action-value ${stats.actionClass}`}>
                {stats.bmi > 0 ? stats.actionText : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default BmiCalculator;